import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/services/firebase';
import { userDoc } from '@/config/collections';
import type { User, UserRole, Business } from '@/types';

// ===========================================
// Types
// ===========================================

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePreferredBusiness: (business: Business) => Promise<void>;
  isOwner: boolean;
  isAdmin: boolean;
  isRep: boolean;
  isOwnerOrAdmin: boolean;
  canAccessAdmin: boolean;
}

// ===========================================
// Context
// ===========================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ===========================================
// Provider Component
// ===========================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    user: null,
    loading: true,
    error: null,
  });

  // Fetch user document from Firestore (using namespaced path)
  const fetchUserDocument = useCallback(async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDocRef = userDoc(db, firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.warn('User document not found in Firestore');
        return null;
      }

      const data = userDocSnap.data();
      return {
        id: userDocSnap.id,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL,
        role: data.role as UserRole,
        status: data.status,
        tenantId: data.tenantId,
        preferredBusiness: data.preferredBusiness || 'azdisaster',
        mondayPersonId: data.mondayPersonId,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('Error fetching user document:', error);
      return null;
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        let user = await fetchUserDocument(firebaseUser);

        if (user && user.status === 'pending') {
          // First login after invite — activate the account
          try {
            await updateDoc(userDoc(db, user.id), {
              status: 'active',
              updatedAt: serverTimestamp(),
            });
            user = { ...user, status: 'active' };
          } catch (err) {
            console.error('Failed to activate pending user:', err);
          }
        } else if (user && user.status !== 'active') {
          // User account is inactive (disabled by admin)
          await firebaseSignOut(auth);
          setState({
            firebaseUser: null,
            user: null,
            loading: false,
            error: 'Your account is not active. Please contact an administrator.',
          });
          return;
        }

        setState({
          firebaseUser,
          user,
          loading: false,
          error: user ? null : 'User profile not found. Please contact an administrator.',
        });
      } else {
        setState({
          firebaseUser: null,
          user: null,
          loading: false,
          error: null,
        });
      }
    });

    return unsubscribe;
  }, [fetchUserDocument]);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change listener will handle the rest
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message.includes('auth/')
            ? 'Invalid email or password'
            : error.message
          : 'An error occurred during sign in';

      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }, []);

  // Update preferred business
  const updatePreferredBusiness = useCallback(
    async (business: Business) => {
      if (!state.user) {
        throw new Error('No user logged in');
      }

      try {
        await updateDoc(userDoc(db, state.user.id), {
          preferredBusiness: business,
          updatedAt: serverTimestamp(),
        });

        setState((prev) => ({
          ...prev,
          user: prev.user ? { ...prev.user, preferredBusiness: business } : null,
        }));
      } catch (error) {
        console.error('Error updating preferred business:', error);
        throw error;
      }
    },
    [state.user]
  );

  // Role checks
  const isOwner = state.user?.role === 'owner';
  const isAdmin = state.user?.role === 'admin';
  const isRep = state.user?.role === 'rep';
  const isOwnerOrAdmin = isOwner || isAdmin;
  const canAccessAdmin = isOwnerOrAdmin;

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    resetPassword,
    updatePreferredBusiness,
    isOwner,
    isAdmin,
    isRep,
    isOwnerOrAdmin,
    canAccessAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ===========================================
// Hook
// ===========================================

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// ===========================================
// Utility: Create initial owner user (for setup)
// ===========================================

export async function createInitialUser(
  firebaseUser: FirebaseUser,
  displayName: string,
  tenantId: string = 'default'
): Promise<void> {
  const userRef = userDoc(db, firebaseUser.uid);
  const existingUser = await getDoc(userRef);

  if (existingUser.exists()) {
    console.log('User already exists');
    return;
  }

  await setDoc(userRef, {
    email: firebaseUser.email,
    displayName,
    role: 'owner',
    status: 'active',
    tenantId,
    preferredBusiness: 'azdisaster',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
